import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, forwardRef, inject } from '@angular/core';
import { GridPlugin } from '@df/catalog-ui/listing/plugin/grid/grid-plugin';
import { ProductListingComponentDef } from '@df/catalog-ui/product/listing/product-listing.component.def';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { GtmImpressionTrackDirective } from '../../../module/gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '../../../module/gtm/gtm-track-product-click.directive';
import { ProductConfigurableOptionsStockService } from '../product/configurable-options/stock/product-configurable-options-stock.service';
import { ProductComponent } from '../product/product.component';
import { ProductListingToolsComponent, toggleTieredPriceModeListingEvent } from './product-listing-tools.component';
import { NListing } from './product-listing.interface';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { LocalProduct } from '../../model/product/product.decorator';
import { ProductListing } from './product-listing.decorator';
import { AdvancedCommerceStore } from '@df/module-advanced-commerce';

declare module '@df/search/search.interfaces' {
    namespace Search {
        interface IResult {
            term?: string;
        }
    }
}
@Component({
    selector: 'product-listing',
    templateUrl: 'product-listing.html',
    styleUrls: ['./product-listing.scss'],
    standalone: true,
    imports: [
        AfterContentInitDirective,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        NgClass,
        ICmsClientModule,
        NgComponentOutlet,
        forwardRef(() => ProductComponent),
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingComponent extends ProductListingComponentDef<LocalProduct<IProductDataV2>> implements OnInit, NListing.IListing {
    protected stockSkus: string[] = [];
    protected preloadProductStockDebounced = this.timeoutService.debounce(() => this.preloadProductStock(), 500);
    canShowTieredPrice: boolean | undefined;
    protected productConfigurableOptionsStockService = inject(ProductConfigurableOptionsStockService);
    protected afterContentInit = inject(AfterContentInitDirectiveService);
    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.afterContentInit.ready.subscribe(value => {
                if (value) {
                    this.detectChanges();
                }
            })
        );
        this.subscriptions.add(
            this.app.events.on(toggleTieredPriceModeListingEvent).subscribe(event => this.onTieredPriceModeChange(event))
        );
    }
    protected override onListingChange(): void {
        if (this.listing) {
            new GridPlugin(this.listing as unknown as ProductListing<Product<IProductDataV2>>);
        }
        super.onListingChange();
        if (this.client.isCms && this.listing?.models?.length) {
            this.preloadProductStockDebounced();
        }
    }
    onTieredPriceModeChange(value: boolean): void {
        this.canShowTieredPrice = value;
        this.detectChanges();
    }
    protected override onListingUpdate() {
        super.onListingUpdate();
        if (this.client.isCms && this.listing?.models?.length) {
            this.preloadProductStockDebounced();
        }
    }
    private async preloadProductStock(): Promise<void> {
        if (!this.listing) {
            return;
        }
        await Promise.all(
            this.listing.models.map(async (model: Product<IProductDataV2>) => {
                const skus = await this.loadDirectChildren(model);
                skus.forEach(sku => {
                    if (this.stockSkus.indexOf(sku) === -1) {
                        this.stockSkus.push(sku);
                    }
                });
            })
        );
        await this.productConfigurableOptionsStockService.loadStocks(this.stockSkus);
    }
    private loadDirectChildren(model: Product<IProductDataV2>): Promise<string[]> {
        return new Promise(resolve => {
            this.app.request.send(
                undefined,
                () => this.app.resources.product.loadAllIds(model.parent.directChildrenIds, 2, { safe: true }),
                () => {
                    const productSkus = model.parent.directChildren.map(i => i.data?.sku).filter(Boolean) as string[];
                    resolve(productSkus);
                },
                resolve
            );
        });
    }

    get clickCode(): string | undefined {
        return undefined;
    }

    get canShow(): boolean {
        return this.ready;
    }

    get ready(): boolean {
        return !!this.listing?.ready;
    }

    get models(): LocalProduct<IProductDataV2>[] {
        return this.listing?.models ?? [];
    }

    get gridSize(): number {
        return this.listing?.plugin?.grid?.gridSize() ?? 0;
    }

    colspan(model: any): number {
        return this.listing?.plugin?.grid?.colspan(model) ?? 0;
    }

    rowspan(model: any): number {
        return this.listing?.plugin?.grid?.rowspan(model) ?? 0;
    }

    get getTotal(): number {
        return this.listing?.getTotal() ?? 0;
    }

    get hasMore(): boolean {
        return this.listing?.hasMore ?? false;
    }

    get visibleQty(): number {
        return this.listing?.visibleQty ?? 0;
    }

    increaseLimit(): void {
        this.listing!.increaseLimit();
    }

    get toolsOutlet() {
        return { component: ProductListingToolsComponent, inputs: { listing: this.listing } };
    }

    get searchTerm(): string | undefined {
        return this.searchResult?.term;
    }

    protected toProductId(model: Product): number {
        return model.id;
    }

    protected toAcItem(model: Product): undefined {
        return undefined;
    }
}
